/**
 * Links in angezeigten Mails (SandboxedMailBody): Was passiert beim Klick?
 *
 * Der Mail-iframe läuft mit `sandbox="allow-same-origin"` — ohne Skripte, ohne
 * Popups. Ein Klick auf einen Link navigierte bisher den iframe SELBST: die
 * fremde Seite erschien mitten im KMVS-Fenster (oft leer, weil sie ohne Skripte
 * nicht läuft), und ein `target="_blank"` blockte die Sandbox still. Der Parent
 * fängt den Klick deshalb ab und entscheidet hier, was daraus wird:
 *
 *  - `extern`: http(s) → neuer Browser-Tab (vom Parent aus, `noopener`).
 *  - `mailto`: KMVS-Verfassen-Fenster mit Empfänger/Betreff vorbelegt.
 *  - `tel`:    an das System übergeben (wie bisher bei Telefonnummern).
 *  - `anker`:  Sprung innerhalb der Mail (`#abschnitt`). In einem `srcdoc`-iframe
 *              löst `#x` gegen die URL der KMVS-Seite auf — ohne Abfangen lüde
 *              der iframe die KMVS-Seite statt zu springen.
 *  - `nichts`: alles andere (relative Pfade, `cid:`, `data:`, `file:` …) — nie
 *              navigieren; der Link bleibt einfach wirkungslos.
 *
 * Die Sandbox bleibt unverändert (kein `allow-popups`, kein `allow-scripts`):
 * Geöffnet wird ausschließlich aus dem Parent heraus.
 */

export type MailtoZiel = {
  to: string[];
  cc: string[];
  bcc: string[];
  subject?: string;
  body?: string;
};

export type MailLinkZiel =
  | { art: "extern"; url: string }
  | { art: "mailto"; url: string; mail: MailtoZiel }
  | { art: "tel"; url: string }
  | { art: "anker"; id: string }
  | { art: "nichts" };

/** Adressliste aus `mailto:` (Komma/Semikolon-getrennt, URL-kodiert). */
function adressen(roh: string | null | undefined): string[] {
  if (!roh) return [];
  let text = roh;
  try {
    text = decodeURIComponent(roh);
  } catch {
    /* kaputte Kodierung: Rohtext nehmen */
  }
  return text
    .split(/[,;]/)
    .map((a) => a.trim())
    .filter(Boolean);
}

/** Zerlegt `mailto:a@b.de?cc=…&subject=…&body=…` (RFC 6068, tolerant). */
export function parseMailto(href: string): MailtoZiel {
  const ohneSchema = href.replace(/^mailto:/i, "");
  const frage = ohneSchema.indexOf("?");
  const pfad = frage >= 0 ? ohneSchema.slice(0, frage) : ohneSchema;
  const query = frage >= 0 ? ohneSchema.slice(frage + 1) : "";
  const ziel: MailtoZiel = { to: adressen(pfad), cc: [], bcc: [] };
  for (const teil of query.split("&")) {
    if (!teil) continue;
    const gleich = teil.indexOf("=");
    const name = (gleich >= 0 ? teil.slice(0, gleich) : teil).toLowerCase();
    const rohWert = gleich >= 0 ? teil.slice(gleich + 1) : "";
    let wert = rohWert;
    try {
      // `+` ist in mailto KEIN Leerzeichen (RFC 6068) — nur %-Kodierung auflösen.
      wert = decodeURIComponent(rohWert);
    } catch {
      /* Rohwert behalten */
    }
    if (name === "to") ziel.to.push(...adressen(rohWert));
    else if (name === "cc") ziel.cc.push(...adressen(rohWert));
    else if (name === "bcc") ziel.bcc.push(...adressen(rohWert));
    else if (name === "subject") ziel.subject = wert;
    else if (name === "body") ziel.body = wert;
  }
  return ziel;
}

/**
 * Bestimmt aus dem ROHEN `href`-Attribut (nicht `a.href` — das wäre bereits
 * gegen die KMVS-Seite aufgelöst) das Ziel eines Klicks.
 */
export function mailLinkZiel(rohHref: string | null | undefined): MailLinkZiel {
  const href = String(rohHref ?? "").trim();
  if (!href) return { art: "nichts" };
  if (href.startsWith("#")) {
    let id = href.slice(1);
    try {
      id = decodeURIComponent(id);
    } catch {
      /* Rohwert */
    }
    return { art: "anker", id };
  }
  // Schemalose Web-Adressen, wie Outlook sie gelegentlich verlinkt.
  const kandidat = /^www\./i.test(href) ? `https://${href}` : href.startsWith("//") ? `https:${href}` : href;
  const schema = /^([a-z][a-z0-9+.-]*):/i.exec(kandidat)?.[1]?.toLowerCase();
  if (schema === "http" || schema === "https") {
    try {
      return { art: "extern", url: new URL(kandidat).href };
    } catch {
      return { art: "nichts" };
    }
  }
  if (schema === "mailto") return { art: "mailto", url: kandidat, mail: parseMailto(kandidat) };
  if (schema === "tel") return { art: "tel", url: kandidat };
  return { art: "nichts" };
}

/** Öffnet eine Web-Adresse in einem neuen Tab, ohne `opener`/`Referer`. */
export function oeffneImNeuenTab(url: string, win: Window = window): void {
  win.open(url, "_blank", "noopener,noreferrer");
}
